import React, { memo } from 'react'
import { ReactComponent as MapMarkerLanes } from 'assets/illustrations/MapMarkerLanes.svg'
import { times } from 'lodash'
import { Table, TableHead, TableRow, TableCell, TableBody } from 'transfix-ui/components/Table'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import { Typography } from 'transfix-ui/core'
import { useMPAnalytics } from 'apps/marketplace/hooks'
import { EQUIPMENT_TYPE_KEY } from 'apps/marketplace/store/transfixLane/types'
import { IRFPLane } from 'apps/marketplace/utils/api/rfpLanes'
import { getLargeEquipmentIcon } from 'apps/marketplace/utils/helpers/getIcon'
import { capitalize } from 'utils/capitalize'
import { EQUIPMENT } from 'constants/equipment'

interface IRFPLaneTableRowProps {
  result: IRFPLane
  selectRFPLane: (result: IRFPLane) => void
  selectedRFPLane?: IRFPLane
}

interface IRFPLaneTableProps {
  laneResults: IRFPLane[]
  renderItem: (result: IRFPLane) => JSX.Element
  loading: boolean
}

export const RFPLaneTableRow = memo<IRFPLaneTableRowProps>(
  ({ result, selectRFPLane, selectedRFPLane }) => {
    const { trackEvent } = useMPAnalytics()
    const {
      deliveryCity,
      deliveryDatMarket,
      deliveryStateCode,
      dropType,
      equipment,
      grossVolume = '',
      id,
      laneId,
      pickupCity,
      pickupDatMarket,
      pickupStateCode,
      rfpTitle,
      salesforceRfpId,
      shipperName,
    } = result
    const { parentId } = equipment || {}
    const equipmentKey = (EQUIPMENT.find(eq => eq.id === parseInt(parentId, 10)) || { key: '' }).key
    const onClickLaneHandler = () => {
      trackEvent('RFP Lanes', 'SELECTED_SEARCH_RESULT', { id: result.id })
      selectRFPLane(result)
    }
    const isSelected = selectedRFPLane?.id === id
    return (
      <TableRow highlightOnHover key={id} selected={isSelected} onClick={onClickLaneHandler}>
        <TableCell type='radio' onChange={onClickLaneHandler} selected={isSelected}>
          {' '}
        </TableCell>
        <TableCell dataTest={`${id}-id`}>{id}</TableCell>
        <TableCell dataTest={`${id}-lane-id`}>{laneId}</TableCell>
        <TableCell dataTest={`${id}-dat-market`}>
          <Tooltip
            position='top'
            id={`${id}-lane`}
            trigger={
              <span data-test={`${id}-specific-lane`}>
                {capitalize(pickupCity)}, {pickupStateCode} - {capitalize(deliveryCity)},{' '}
                {deliveryStateCode}
              </span>
            }>
            {pickupDatMarket} - {deliveryDatMarket}
          </Tooltip>
        </TableCell>
        <TableCell dataTest={`${id}-shipper-name`}>{shipperName}</TableCell>
        <TableCell>
          {salesforceRfpId ? (
            <Tooltip
              position='top'
              id={`${id}-rfp`}
              data-test={`${id}-salesforce-rfp-id`}
              trigger={<span data-test={`${id}-rfp-title`}>{rfpTitle}</span>}>
              Salesforce RFP ID: {salesforceRfpId}
            </Tooltip>
          ) : (
            <span data-test={`${id}-rfp-title`}>{rfpTitle}</span>
          )}
        </TableCell>
        <TableCell dataTest={`${id}-equipment-type`}>
          {getLargeEquipmentIcon(equipmentKey as EQUIPMENT_TYPE_KEY)}
        </TableCell>
        <TableCell dataTest={`${id}-drop-type`}>{dropType}</TableCell>
        <TableCell dataTest={`${id}-volume-relevant`}>{grossVolume}</TableCell>
      </TableRow>
    )
  }
)

export const RFPLaneTable = memo<IRFPLaneTableProps>(({ laneResults, loading, renderItem }) => {
  const skeletonLoaderRows = times(5, String)
  const skeletonLoaderColumns = times(9, String)
  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell> </TableCell>
          <TableCell>RFP Lane ID</TableCell>
          <TableCell>Lane ID</TableCell>
          <TableCell>Shipper Specific Lane</TableCell>
          <TableCell>Shipper Name</TableCell>
          <TableCell>RFP Title</TableCell>
          <TableCell>Equipment</TableCell>
          <TableCell>Drop Type</TableCell>
          <TableCell>Volume Relevant</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {loading ? (
          <>
            {skeletonLoaderRows.map((row, j) => (
              // eslint-disable-next-line react/no-array-index-key
              <TableRow key={`${row}-${j}`}>
                {skeletonLoaderColumns.map((col, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <TableCell key={`${col}-${i}`}>
                    <span className='skeleton-loader' />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </>
        ) : (
          <>
            {laneResults.length > 0 ? (
              laneResults.map(result => renderItem(result))
            ) : (
              <TableRow>
                <TableCell colSpan={7}>
                  <div className='flex flex-column items-center justify-center'>
                    <MapMarkerLanes />
                    <Typography variant='h2'>No Lanes Found</Typography>
                    <p>Try adjusting search and filter settings</p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </>
        )}
      </TableBody>
    </Table>
  )
})
